import { logger } from '@magicschool/logger';
import type { SupabaseClient } from '@supabase/supabase-js';
import { createBrowserClient } from '../clients/browser';
import type { Enums, FileMetadata, RoomStudentTableRow, Tables } from '../types';
import { channels, events } from './channels';

export type NewToolChatMessage = {
  roomStudentId: string;
  messageData: RoomStudentTableRow;
};

export const broadcastNewToolChat = (
  roomId: string,
  roomStudentId: string,
  messageData: RoomStudentTableRow,
  supabaseClientOverride?: SupabaseClient,
) => {
  const payload: NewToolChatMessage = { roomStudentId, messageData };
  generalBroadcast(roomId, 'teacherRoomChannel', 'toolChat', payload, supabaseClientOverride);
};

export const broadcastFlaggedMessage = (
  roomId: string,
  moderation: Tables<'message_moderation_data'>,
  supabaseClientOverride?: SupabaseClient,
) => {
  generalBroadcast(roomId, 'teacherRoomChannel', 'flaggedMessage', moderation, supabaseClientOverride);
};

export type RoomStateChangeMessage = { state: Enums<'room_state'> };
export const broadcastRoomStateChange = (roomId: string, state: Enums<'room_state'>, supabaseClientOverride?: SupabaseClient) => {
  const payload: RoomStateChangeMessage = { state };
  generalBroadcast(roomId, 'studentRoomChannel', 'roomState', payload, supabaseClientOverride);
};

export type ReceiveFileMessage = { file: FileMetadata };
export const broadcastReceiveFile = (channelId: string, file: FileMetadata, supabaseClientOverride?: SupabaseClient) => {
  const payload: ReceiveFileMessage = { file };
  generalBroadcast(channelId, 'attachFileServerChannel', 'attachFile', payload, supabaseClientOverride);
};

export const broadcastAttachFileModalClosed = (channelId: string, supabaseClientOverride?: SupabaseClient) => {
  const payload: { modalClosed: boolean } = { modalClosed: true };
  generalBroadcast(channelId, 'attachFileClientChannel', 'attachFileModalClosed', payload, supabaseClientOverride);
};

// biome-ignore lint/complexity/noBannedTypes: this truly can be empty
export type ReceiveFileJoinedMessage = {};
export const broadcastAttachFileJoined = (channelId: string, supabaseClientOverride?: SupabaseClient) => {
  generalBroadcast(channelId, 'attachFileServerChannel', 'attachFileJoined', {}, supabaseClientOverride);
};

export const broadcastAttachFilePingPong = (channelId: string, kind: 'ping' | 'pong', supabaseClientOverride?: SupabaseClient) => {
  const data = {
    channelId,
    channelName: kind === 'ping' ? 'attachFileServerChannel' : 'attachFileClientChannel',
    eventName: kind === 'ping' ? 'attachFileJoinedPing' : 'attachFileJoinedPong',
    payload: { kind, timestamp: Date.now() },
  } as const;
  generalBroadcast(data.channelId, data.channelName, data.eventName, data.payload, supabaseClientOverride);
};

export type RoomStudentStateChangeMessage = {
  state: Enums<'room_state'>;
  roomStudentId: string;
};

export const broadcastRoomStudentStateChange = (
  roomId: string,
  roomStudentId: string,
  state: Enums<'room_state'>,
  supabaseClientOverride?: SupabaseClient,
) => {
  const payload: RoomStudentStateChangeMessage = { state, roomStudentId };
  generalBroadcast(roomId, 'studentRoomChannel', 'roomStudentState', payload, supabaseClientOverride);
};

export type RoomStudentJoinedMessage = { roomStudentId: string };
export const broadcastStudentJoinedRoom = (roomId: string, roomStudentId: string, supabaseClientOverride?: SupabaseClient) => {
  const payload: RoomStudentJoinedMessage = { roomStudentId };
  generalBroadcast(roomId, 'teacherRoomChannel', 'roomStudentJoined', payload, supabaseClientOverride);
};

export type MessagePayload<T> = {
  payload: T;
};

export type GradingStartedMessage = {
  submissionId: string;
  feedbackTypes: string[];
  startedAt: string;
};

export type GradingProgressMessage = {
  submissionId: string;
  feedbackType: string;
  status: 'processing' | 'completed';
  progress: number; // 0-100
};

export type GradingCompletedMessage = {
  submissionId: string;
  completedAt: string;
};

export type GradingErrorMessage = {
  submissionId: string;
  error: string;
  failedAt: string;
};

export type ReviewerBatchStartedMessage = {
  reviewerId: string;
  totalSubmissions: number;
  startedAt: string;
};

export type ReviewerBatchCompletedMessage = {
  reviewerId: string;
  successfulSubmissions: number;
  failedSubmissions: number;
  completedAt: string;
};

export type ReviewerCompletedMessage = {
  reviewerId: string;
  insightsGenerated: number;
  completedAt: string;
};

export type ReviewerErrorMessage = {
  reviewerId: string;
  error: string;
  failedAt: string;
  stage: 'grading' | 'insights';
};

export const broadcastGrading = <T = GradingCompletedMessage | GradingErrorMessage | GradingProgressMessage | GradingStartedMessage>(
  reviewerId: string,
  submissionId: string,
  event: keyof typeof events,
  payload: T,
  supabaseClientOverride?: SupabaseClient,
) => {
  generalBroadcast(reviewerId, 'reviewerGradingChannel', event, payload, supabaseClientOverride);
  generalBroadcast(submissionId, 'submissionGradingChannel', event, payload, supabaseClientOverride);
};

export const broadcastReviewerStatus = <T>(
  reviewerId: string,
  event: 'reviewerBatchStarted' | 'reviewerBatchCompleted' | 'reviewerCompleted' | 'reviewerError',
  payload: T,
  supabaseClientOverride?: SupabaseClient,
) => {
  generalBroadcast(reviewerId, 'reviewerGradingChannel', event, payload, supabaseClientOverride);
};

export type QuizStudentJoinedMessage = {
  attemptId: string;
  studentId: string;
  studentName: string;
  joinedAt: string;
};

export const broadcastQuizStudentJoined = (
  assignmentId: string,
  attemptId: string,
  studentId: string,
  studentName: string,
  supabaseClientOverride?: SupabaseClient,
) => {
  const payload: QuizStudentJoinedMessage = {
    attemptId,
    studentId,
    studentName,
    joinedAt: new Date().toISOString(),
  };
  generalBroadcast(assignmentId, 'teacherQuizChannel', 'quizStudentJoined', payload, supabaseClientOverride);
};

export type QuizAnswersUpdatedMessage = {
  attemptId: string;
  studentId: string;
  answers: unknown;
  updatedAt: string;
};

export const broadcastQuizAnswersUpdated = (
  assignmentId: string,
  attemptId: string,
  studentId: string,
  answers: unknown,
  supabaseClientOverride?: SupabaseClient,
) => {
  const payload: QuizAnswersUpdatedMessage = {
    attemptId,
    studentId,
    answers,
    updatedAt: new Date().toISOString(),
  };
  generalBroadcast(assignmentId, 'teacherQuizChannel', 'quizAnswersUpdated', payload, supabaseClientOverride);
};

export type QuizFinishedMessage = {
  attemptId: string;
  studentId: string;
  studentName: string;
  answers: unknown;
  finishedAt: string;
};

export const broadcastQuizFinished = (
  assignmentId: string,
  attemptId: string,
  studentId: string,
  studentName: string,
  answers: unknown,
  supabaseClientOverride?: SupabaseClient,
) => {
  const payload: QuizFinishedMessage = {
    attemptId,
    studentId,
    studentName,
    answers,
    finishedAt: new Date().toISOString(),
  };
  generalBroadcast(assignmentId, 'teacherQuizChannel', 'quizFinished', payload, supabaseClientOverride);
};

export type QuizCompletionProcessedMessage = {
  assignmentId: string;
  completedAt: string;
  completedAttempts: number;
};

/**
 * Awaitable version of broadcastQuizCompletionProcessed for use in Trigger.dev tasks
 * where we need to ensure the message is sent before the task completes
 */
export const broadcastQuizCompletionProcessedAsync = (
  assignmentId: string,
  completedAttempts: number,
  supabaseClientOverride?: SupabaseClient,
): Promise<void> => {
  const payload: QuizCompletionProcessedMessage = {
    assignmentId,
    completedAttempts,
    completedAt: new Date().toISOString(),
  };
  return generalBroadcastAsync(assignmentId, 'teacherQuizChannel', 'quizCompletionProcessed', payload, supabaseClientOverride);
};

const generalBroadcast = <T>(
  roomId: string,
  channelName: keyof typeof channels,
  event: keyof typeof events,
  payload: T,
  supabaseClientOverride?: SupabaseClient,
) => {
  // There's a feature that's not yet available on local to use rest apis
  // instead of web sockets, so we're using the web sockets for now.
  logger.info(`broadcasting to ${channelName} event: ${events[event]}`, roomId, payload);
  const client = supabaseClientOverride || createBrowserClient();

  const tempChannel = client.channel(channels[channelName](roomId));
  tempChannel.subscribe(async (status) => {
    if (status !== 'SUBSCRIBED') return;
    const typedPayload: MessagePayload<T> = { payload };
    await tempChannel.send({
      type: 'broadcast',
      event: events[event],
      payload: typedPayload,
    });
    tempChannel.unsubscribe();
  });
};

/**
 * Awaitable version of generalBroadcast for use in contexts where we need to
 * ensure the message is sent before returning (e.g., Trigger.dev tasks)
 */
const generalBroadcastAsync = <T>(
  roomId: string,
  channelName: keyof typeof channels,
  event: keyof typeof events,
  payload: T,
  supabaseClientOverride?: SupabaseClient,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    logger.info(`broadcasting (async) to ${channelName} event: ${events[event]}`, roomId, payload);
    const client = supabaseClientOverride || createBrowserClient();

    const tempChannel = client.channel(channels[channelName](roomId));
    let settled = false;

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      tempChannel.unsubscribe();
      reject(new Error(`Broadcast timeout: failed to subscribe to ${channelName}`));
    }, 5000);

    tempChannel.subscribe(async (status) => {
      if (status !== 'SUBSCRIBED') return;
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      try {
        const typedPayload: MessagePayload<T> = { payload };
        await tempChannel.send({
          type: 'broadcast',
          event: events[event],
          payload: typedPayload,
        });
        tempChannel.unsubscribe();
        resolve();
      } catch (error) {
        tempChannel.unsubscribe();
        reject(error);
      }
    });
  });
};

// I cannot explain how this is happening, but supabase is sometimes
// wrapping payload in an extra payload object, and sometimes not...
// ... It makes me scare too.
export function handleIntermittentPayloadIssue<T>(payload: T) {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  let internalPayload: any = payload;

  // Supabase can nest payloads multiple levels deep; unwrap until we reach the data.
  while (internalPayload && typeof internalPayload === 'object' && 'payload' in internalPayload) {
    internalPayload = internalPayload.payload;
  }

  return internalPayload as T;
}
